import { ACTOR_ID_HEADER_KEY, CARE_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const BASE = `${CARE_SERVICE_BASE_URL}/api/v1`;
const USE_LOCAL_CARE_PROXY = CARE_SERVICE_BASE_URL.startsWith("/");

const localCareProxyOrigin = () => {
  if (!USE_LOCAL_CARE_PROXY) return undefined;
  return typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "";
};

const careRequestConfig = (config = {}) => {
  if (!USE_LOCAL_CARE_PROXY) return config;
  return {
    ...config,
    baseURL: localCareProxyOrigin(),
  };
};

const unwrap = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data !== undefined) return payload.data;
  if (payload?.result !== undefined) return payload.result;
  return payload;
};

const pendingLists = new Map();
const pendingSnapshots = new Map();
const getSnapshot = (careProfileId, type) => {
  const profileId = requiredId(careProfileId, "CareProfile id");
  const snapshotType = requiredId(type, "Snapshot type");
  const key = `${profileId}:${snapshotType}`;
  const pending = pendingSnapshots.get(key);
  if (pending) return pending;

  const request = apiClient
    .get(`${BASE}/care-profiles/${profileId}/snapshots/${snapshotType}`, careRequestConfig({
      params: { _ts: Date.now() },
      headers: {
        "Cache-Control": "no-cache, no-store",
        Pragma: "no-cache",
      },
    }))
    .then((response) => response?.data)
    .finally(() => {
      if (pendingSnapshots.get(key) === request) pendingSnapshots.delete(key);
    });
  pendingSnapshots.set(key, request);
  return request;
};
const list = (path, params = {}) => {
  const key = `${path}:${JSON.stringify(
    Object.entries(params).sort(([left], [right]) => left.localeCompare(right)),
  )}`;
  const pending = pendingLists.get(key);
  if (pending) return pending;

  const request = apiClient
    .get(`${BASE}${path}`, careRequestConfig({ params }))
    .then(unwrap)
    .finally(() => {
      if (pendingLists.get(key) === request) pendingLists.delete(key);
    });
  pendingLists.set(key, request);
  return request;
};
const pendingMiniLists = new Map();
const miniList = (path, params = {}) => {
  const key = `${path}:${JSON.stringify(
    Object.entries(params).sort(([left], [right]) => left.localeCompare(right)),
  )}`;
  const pending = pendingMiniLists.get(key);
  if (pending) return pending;

  const request = list(path, params).finally(() => {
    if (pendingMiniLists.get(key) === request) pendingMiniLists.delete(key);
  });
  pendingMiniLists.set(key, request);
  return request;
};
const pendingGets = new Map();
const get = (path) => {
  const pending = pendingGets.get(path);
  if (pending) return pending;

  const request = apiClient
    .get(`${BASE}${path}`, careRequestConfig())
    .then(unwrap)
    .finally(() => {
      if (pendingGets.get(path) === request) pendingGets.delete(path);
    });
  pendingGets.set(path, request);
  return request;
};
const post = (path, data) =>
  apiClient.post(`${BASE}${path}`, data, careRequestConfig()).then(unwrap);
const put = (path, data) =>
  apiClient.put(`${BASE}${path}`, data, careRequestConfig()).then(unwrap);
const patch = (path, data) =>
  apiClient.patch(`${BASE}${path}`, data, careRequestConfig()).then(unwrap);
const remove = (path) =>
  apiClient.delete(`${BASE}${path}`, careRequestConfig()).then(unwrap);
const getRaw = (path) =>
  apiClient
    .get(`${BASE}${path}`, careRequestConfig())
    .then((response) => response?.data);
const multipartCareRequestConfig = (config = {}) => {
  const nextConfig = careRequestConfig(config);
  const headers = { ...(nextConfig.headers || {}) };
  delete headers["Content-Type"];
  delete headers["content-type"];
  return {
    ...nextConfig,
    headers,
  };
};
const actorConfig = (actorId, config = {}) =>
  careRequestConfig({
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {}),
    },
  });
const requiredId = (value, label) => {
  if (value === undefined || value === null || value === "") {
    throw new Error(`${label} is required.`);
  }
  return encodeURIComponent(value);
};
const closedAssessmentProcessStatuses = new Set([
  "ARCHIVED",
  "COMPLETED",
  "COMPLETE",
  "DECLINED",
  "CANCELED",
  "CANCELLED",
  "DELETED",
]);
const assessmentProcessStatus = (process = {}) =>
  String(
    process.workflowStatus || process.processStatus || process.status || "",
  )
    .trim()
    .toUpperCase();
const assessmentProcessUpdatedAt = (process = {}) => {
  const date = new Date(
    process.updatedAt ||
      process.createdAt ||
      process.processStartDate ||
      process.startDate ||
      0,
  );
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};
const activeAssessmentProcessFromList = (processes = []) =>
  (Array.isArray(processes) ? processes : [])
    .filter((process) => {
      const status = assessmentProcessStatus(process);
      return (
        process && (!status || !closedAssessmentProcessStatuses.has(status))
      );
    })
    .sort(
      (a, b) => assessmentProcessUpdatedAt(b) - assessmentProcessUpdatedAt(a),
    )[0] || null;
const createAssessmentProcess = async (data = {}) => {
  if (data?.residentId) {
    const existingProcesses = await list("/assessment-processes", {
      residentId: data.residentId,
    });
    const activeProcess = activeAssessmentProcessFromList(existingProcesses);
    if (activeProcess) return activeProcess;
  }
  return post("/assessment-processes", data);
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const pollOnboardingAssessmentImport = async (
  jobId,
  { intervalMs = 2500, timeoutMs = 15 * 60 * 1000 } = {},
) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const job = await getRaw(
      `/residents/onboarding/import-assessment/jobs/${requiredId(jobId, "Assessment import job id")}`,
    );
    const status = String(job?.status || "").toUpperCase();
    if (status === "COMPLETED") {
      return job.result || job.extraction || job.data;
    }
    if (status === "FAILED") {
      throw new Error(job.message || "Assessment extraction failed.");
    }
    await wait(intervalMs);
  }
  throw new Error(
    "Assessment extraction is taking longer than expected. Try again with fewer pages or a smaller file.",
  );
};

export const careService = {
  dashboard: (params = {}, actorId) =>
    apiClient
      .get(`${BASE}/dashboard`, actorConfig(actorId, { params }))
      .then(unwrap),

  residents: {
    list: ({ actorId, ...params } = {}) =>
      apiClient
        .get(`${BASE}/residents`, actorConfig(actorId, { params }))
        .then(unwrap),
    listMini: (params) => miniList("/residents/mini", params),
    search: (params) => list("/residents/search", params),
    quickSearch: (params) => list("/residents/quick-search", params),
    get: (residentId) =>
      get(`/residents/${requiredId(residentId, "Resident id")}`),
    create: (data) => post("/residents", data),
    register: (data) => post("/residents/registrations", data),
    listAuditLogs: (residentId, { actorId, ...params } = {}) =>
      apiClient
        .get(
          `${BASE}/residents/${requiredId(residentId, "Resident id")}/audit-logs`,
          actorConfig(actorId, { params }),
        )
        .then(unwrap),
    importOnboardingAssessment: (files = []) => {
      const formData = new FormData();
      (Array.isArray(files) ? files : [files])
        .filter(Boolean)
        .forEach((file) => formData.append("pages", file, file.name));
      return apiClient
        .post(
          `${BASE}/residents/onboarding/import-assessment`,
          formData,
          multipartCareRequestConfig({
            timeout: 30 * 60 * 1000,
          }),
        )
        .then(unwrap)
        .then((job) =>
          job?.jobId ? pollOnboardingAssessmentImport(job.jobId) : job,
        );
    },
    createOnboardingBio: (data) => post("/residents/onboarding/bio", data),
    updateOnboardingBio: (residentId, data) =>
      put(
        `/residents/${requiredId(residentId, "Resident id")}/onboarding/bio`,
        data,
      ),
    saveOnboardingContacts: (residentId, data) =>
      put(
        `/residents/${requiredId(residentId, "Resident id")}/onboarding/contacts`,
        data,
      ),
    saveOnboardingCareHistory: (residentId, data) =>
      put(
        `/residents/${requiredId(residentId, "Resident id")}/onboarding/care-history`,
        data,
      ),
    saveOnboardingCareSnapshot: (residentId, data) =>
      put(
        `/residents/${requiredId(residentId, "Resident id")}/onboarding/care-snapshot`,
        data,
      ),
    saveOnboardingDocuments: (residentId, data) =>
      put(
        `/residents/${requiredId(residentId, "Resident id")}/onboarding/documents`,
        data,
      ),
    saveResidencyDetails: (residentId, data) =>
      put(
        `/residents/${requiredId(residentId, "Resident id")}/residency-details`,
        data,
      ),
    getOnboardingPreview: (residentId) =>
      get(
        `/residents/${requiredId(residentId, "Resident id")}/onboarding/preview`,
      ),
    confirmOnboarding: (residentId, data) =>
      post(
        `/residents/${requiredId(residentId, "Resident id")}/onboarding/confirm`,
        data,
      ),
    listRegistrations: (params) => list("/residents/registrations", params),
    getRegistration: (registrationId) =>
      get(`/residents/registrations/${encodeURIComponent(registrationId)}`),
    update: (residentId, data) =>
      put(`/residents/${requiredId(residentId, "Resident id")}`, data),
    delete: (residentId) =>
      remove(`/residents/${requiredId(residentId, "Resident id")}`),
  },

  residentContacts: {
    listForResident: (residentId) =>
      list(
        `/residents/${requiredId(residentId, "Resident id")}/collateral-contacts`,
      ),
    replaceForResident: (residentId, data) =>
      put(
        `/residents/${requiredId(residentId, "Resident id")}/collateral-contacts`,
        data,
      ),
    createForResident: (residentId, data) =>
      post(
        `/residents/${requiredId(residentId, "Resident id")}/collateral-contacts`,
        data,
      ),
    updateForResident: (residentId, contactId, data) =>
      put(
        `/residents/${requiredId(residentId, "Resident id")}/collateral-contacts/${requiredId(contactId, "Contact id")}`,
        data,
      ),
    deleteForResident: (residentId, contactId) =>
      remove(
        `/residents/${requiredId(residentId, "Resident id")}/collateral-contacts/${requiredId(contactId, "Contact id")}`,
      ),
  },
  residentContact: {
    get: (residentId) =>
      get(
        `/residents/${requiredId(residentId, "Resident id")}/resident-contact`,
      ),
    create: (residentId, data) =>
      post(
        `/residents/${requiredId(residentId, "Resident id")}/resident-contact`,
        data,
      ),
    update: (residentId, data) =>
      put(
        `/residents/${requiredId(residentId, "Resident id")}/resident-contact`,
        data,
      ),
    delete: (residentId) =>
      remove(
        `/residents/${requiredId(residentId, "Resident id")}/resident-contact`,
      ),
  },
  residentVitalSigns: {
    listForResident: (residentId, params) =>
      list(
        `/residents/${requiredId(residentId, "Resident id")}/vital-signs`,
        params,
      ),
    createForResident: (residentId, data, params) =>
      apiClient
        .post(
          `${BASE}/residents/${requiredId(residentId, "Resident id")}/vital-signs`,
          {
            ...(data || {}),
            residentId: requiredId(residentId, "Resident id"),
          },
          careRequestConfig({ params }),
        )
        .then(unwrap),
  },
  careActivityLogs: {
    listForResident: (residentId, params) =>
      list(
        `/residents/${requiredId(residentId, "Resident id")}/care-activity-logs`,
        params,
      ),
  },
  residentVitalSignThresholds: {
    listForResident: (residentId, params) =>
      list(
        `/residents/${requiredId(residentId, "Resident id")}/vital-sign-thresholds`,
        params,
      ),
    saveForResident: (residentId, thresholdKey, data, params) =>
      apiClient
        .put(
          `${BASE}/residents/${requiredId(residentId, "Resident id")}/vital-sign-thresholds/${requiredId(thresholdKey, "Threshold key")}`,
          data,
          careRequestConfig({ params }),
        )
        .then(unwrap),
  },
  residentMedications: {
    list: (params) => list("/medications", params),
    create: (data, params) =>
      apiClient
        .post(`${BASE}/medications`, data, careRequestConfig({ params }))
        .then(unwrap),
    update: (medicationId, data, params) =>
      apiClient
        .put(
          `${BASE}/medications/${requiredId(medicationId, "Medication id")}`,
          data,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    delete: (medicationId, params) =>
      apiClient
        .delete(
          `${BASE}/medications/${requiredId(medicationId, "Medication id")}`,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    expected: (params) => list("/medications/expected", params),
    listAdministrationLogs: (residentIdOrParams, maybeParams) => {
      if (maybeParams !== undefined) {
        return list("/medications/administration-logs", {
          ...(maybeParams || {}),
          residentId: requiredId(residentIdOrParams, "Resident id"),
        });
      }
      return list("/medications/administration-logs", residentIdOrParams);
    },
    createAdministrationLog: (residentIdOrData, dataOrParams, maybeParams) => {
      if (maybeParams !== undefined) {
        return apiClient
          .post(
            `${BASE}/medications/administration-logs`,
            {
              ...(dataOrParams || {}),
              residentId: requiredId(residentIdOrData, "Resident id"),
            },
            careRequestConfig({ params: maybeParams }),
          )
          .then(unwrap);
      }
      return apiClient
        .post(
          `${BASE}/medications/administration-logs`,
          residentIdOrData,
          careRequestConfig({ params: dataOrParams }),
        )
        .then(unwrap);
    },
    cancelAdministrationLog: (logId, data, params) =>
      apiClient
        .put(
          `${BASE}/medications/administration-logs/${requiredId(logId, "Administration log id")}/cancel`,
          data,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    listForResident: (residentId, params) =>
      list("/medications", {
        ...(params || {}),
        residentId: requiredId(residentId, "Resident id"),
      }),
    createForResident: (residentId, data, params) =>
      apiClient
        .post(
          `${BASE}/medications`,
          {
            ...(data || {}),
            residentId: requiredId(residentId, "Resident id"),
          },
          careRequestConfig({ params }),
        )
        .then(unwrap),
    expectedForResident: (residentId, params) =>
      list("/medications/expected", {
        ...(params || {}),
        residentId: requiredId(residentId, "Resident id"),
      }),
    listAdministrationLogsForResident: (residentId, params) =>
      list("/medications/administration-logs", {
        ...(params || {}),
        residentId: requiredId(residentId, "Resident id"),
      }),
    createAdministrationLogForResident: (residentId, data, params) =>
      apiClient
        .post(
          `${BASE}/medications/administration-logs`,
          {
            ...(data || {}),
            residentId: requiredId(residentId, "Resident id"),
          },
          careRequestConfig({ params }),
        )
        .then(unwrap),
    listRefillRequests: (residentId, params) =>
      list("/medications/refill-requests", {
        ...(params || {}),
        residentId: requiredId(residentId, "Resident id"),
      }),
    createRefillRequest: (residentId, data, params) =>
      apiClient
        .post(
          `${BASE}/medications/refill-requests`,
          {
            ...(data || {}),
            residentId: requiredId(residentId, "Resident id"),
          },
          careRequestConfig({ params }),
        )
        .then(unwrap),
    listNewMedications: (residentId, params) =>
      list("/medications/new-medications", {
        ...(params || {}),
        residentId: requiredId(residentId, "Resident id"),
      }),
    importPrescription: (residentId, file, document, params, actorId) => {
      const data = new FormData();
      data.append("file", file);
      data.append("document", JSON.stringify(document));
      return apiClient.post(`${BASE}/medications/new-medications/import`, data,
        multipartCareRequestConfig({
          params: { ...(params || {}), residentId: requiredId(residentId, "Resident id") },
          headers: { [ACTOR_ID_HEADER_KEY]: requiredId(actorId, "Actor id") },
          timeout: 330000,
        })).then(unwrap);
    },
    simulateReceivedPrescription: (residentId, data, params) =>
      apiClient
        .post(
          `${BASE}/medications/new-medications/simulate-received-prescription`,
          {
            ...(data || {}),
            residentId: requiredId(residentId, "Resident id"),
          },
          careRequestConfig({ params }),
        )
        .then(unwrap),
    updateNewMedication: (newMedicationId, data, params) =>
      apiClient
        .put(
          `${BASE}/medications/new-medications/${requiredId(newMedicationId, "New medication id")}`,
          data,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    approveNewMedicationForAdf: (newMedicationId, params) =>
      apiClient
        .put(
          `${BASE}/medications/new-medications/${requiredId(newMedicationId, "New medication id")}/oliveeb-approve`,
          null,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    activateNewMedication: (newMedicationId, params) =>
      apiClient
        .put(
          `${BASE}/medications/new-medications/${requiredId(newMedicationId, "New medication id")}/adf-approve`,
          null,
          careRequestConfig({ params }),
        )
        .then(unwrap),
  },
  pharmacies: {
    list: (params) => list("/pharmacies", params),
    get: (pharmacyId, params) =>
      list(`/pharmacies/${requiredId(pharmacyId, "Pharmacy id")}`, params),
    create: (data, params) =>
      apiClient
        .post(`${BASE}/pharmacies`, data, careRequestConfig({ params }))
        .then(unwrap),
    update: (pharmacyId, data, params) =>
      apiClient
        .put(
          `${BASE}/pharmacies/${requiredId(pharmacyId, "Pharmacy id")}`,
          data,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    listForResident: (residentId, params) =>
      list(
        `/pharmacies/residents/${requiredId(residentId, "Resident id")}`,
        params,
      ),
    addToResident: (residentId, data, params) =>
      apiClient
        .post(
          `${BASE}/pharmacies/residents/${requiredId(residentId, "Resident id")}`,
          data,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    removeFromResident: async (residentId, pharmacyId, params) => {
      const resident = requiredId(residentId, "Resident id");
      const pharmacy = requiredId(pharmacyId, "Pharmacy id");
      const path = `${BASE}/pharmacies/residents/${resident}/${pharmacy}`;
      try {
        return await apiClient
          .delete(path, careRequestConfig({ params }))
          .then(unwrap);
      } catch (err) {
        const message = String(err?.response?.data?.message || "");
        const canRetry =
          err?.response?.status === 404 ||
          err?.response?.status === 405 ||
          message.includes("route is not available");
        if (!canRetry) throw err;
        return apiClient
          .post(`${path}/remove`, {}, careRequestConfig({ params }))
          .then(unwrap);
      }
    },
  },
  emergencyEvacuationDrills: {
    list: (params) => list("/emergency-evacuation-drills", params),
    get: (emergencyEvacuationDrillId, params) =>
      list(
        `/emergency-evacuation-drills/${requiredId(emergencyEvacuationDrillId, "Emergency evacuation drill id")}`,
        params,
      ),
    create: (data, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .post(
          `${BASE}/emergency-evacuation-drills`,
          data,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
    update: (emergencyEvacuationDrillId, data, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .put(
          `${BASE}/emergency-evacuation-drills/${requiredId(emergencyEvacuationDrillId, "Emergency evacuation drill id")}`,
          data,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
    delete: (emergencyEvacuationDrillId, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .delete(
          `${BASE}/emergency-evacuation-drills/${requiredId(emergencyEvacuationDrillId, "Emergency evacuation drill id")}`,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
  },
  assessmentProcesses: {
    list: (params) => list("/assessment-processes", params),
    listMini: (params) => miniList("/assessment-processes/mini", params),
    get: (assessmentProcessId) =>
      get(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}`),
    create: createAssessmentProcess,
    update: (assessmentProcessId, data) =>
      put(
        `/assessment-processes/${encodeURIComponent(assessmentProcessId)}`,
        data,
      ),
    updateStatus: (assessmentProcessId, data) =>
      patch(
        `/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/status`,
        data,
      ),
    delete: (assessmentProcessId) =>
      remove(
        `/assessment-processes/${encodeURIComponent(assessmentProcessId)}`,
      ),
  },

  residentAssessments: {
    list: (params) => list("/resident-assessments", params),
    get: (assessmentId) =>
      get(`/resident-assessments/${requiredId(assessmentId, "Assessment id")}`),
    listForResident: (residentId) =>
      list(`/residents/${requiredId(residentId, "Resident id")}/assessments`),
    createForResident: (residentId, data) =>
      post(
        `/residents/${requiredId(residentId, "Resident id")}/assessments`,
        data,
      ),
    update: (assessmentId, data) =>
      put(
        `/residents/assessments/${requiredId(assessmentId, "Assessment id")}`,
        data,
      ),
    updateStatus: (assessmentId, data) =>
      patch(
        `/resident-assessments/${requiredId(assessmentId, "Assessment id")}/status`,
        data,
      ),
    updatePages: (assessmentId, data) =>
      put(
        `/resident-assessments/${requiredId(assessmentId, "Assessment id")}/pages`,
        data,
      ),
  },

  assessmentRecords: {
    list: (params) => list("/assessment-records", params),
    get: (recordId) =>
      get(`/assessment-records/${encodeURIComponent(recordId)}`),
    byProcessAndType: (assessmentProcessId, recordType) =>
      get(
        `/assessment-processes/${encodeURIComponent(assessmentProcessId)}/records/${encodeURIComponent(recordType)}`,
      ),
    saveForProcess: (assessmentProcessId, recordType, data) =>
      put(
        `/assessment-processes/${encodeURIComponent(assessmentProcessId)}/records/${encodeURIComponent(recordType)}`,
        data,
      ),
    submitDailyNoteForProcess: (assessmentProcessId, recordType, data) =>
      post(
        `/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/records/${requiredId(recordType, "Record type")}/daily-notes`,
        data,
      ),
    deleteDailyNoteForProcess: (assessmentProcessId, recordType, noteId) =>
      remove(
        `/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/records/${requiredId(recordType, "Record type")}/daily-notes/${requiredId(noteId, "Daily note id")}`,
      ),
    savePagesForProcess: (assessmentProcessId, recordType, data) =>
      put(
        `/assessment-processes/${encodeURIComponent(assessmentProcessId)}/records/${encodeURIComponent(recordType)}/pages`,
        data,
      ),
  },

  meetAndGreet: {
    getForProcess: (assessmentProcessId) =>
      get(
        `/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/meet-and-greet`,
      ),
    saveForProcess: (assessmentProcessId, data) =>
      put(
        `/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/meet-and-greet`,
        data,
      ),
  },

  providerDecision: {
    saveForProcess: (assessmentProcessId, data) =>
      put(
        `/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/provider-decision`,
        data,
      ),
  },

  negotiatedCarePlans: {
    template: () => get("/assessment-processes/negotiated-care-plan/template"),
    list: (params = {}, actorId) =>
      apiClient
        .get(
          `${BASE}/residents/care-plans/negotiated`,
          actorConfig(actorId, { params }),
        )
        .then(unwrap),
    get: (planId) =>
      get(`/negotiated-care-plans/${encodeURIComponent(planId)}`),
    getForProcess: (assessmentProcessId) =>
      get(
        `/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan`,
      ),
    saveForProcess: (assessmentProcessId, data) =>
      put(
        `/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan`,
        data,
      ),
    generate: (assessmentProcessId, data) =>
      post(
        `/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan/generate`,
        data,
      ),
    refine: (assessmentProcessId, data) =>
      post(
        `/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan/refine`,
        data,
      ),
    format: (assessmentProcessId, data) =>
      post(
        `/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan/format`,
        data,
      ),
  },

  dailyNotes: {
    list: (params) => list("/daily-notes", params),
    generateWeeklyReport: (params) =>
      apiClient
        .post(
          `${BASE}/daily-notes/weekly-report`,
          null,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    listWeeklyReports: (params) => list("/daily-notes/weekly-reports", params),
    updateWeeklyReport: (reportId, params, data) =>
      apiClient
        .put(
          `${BASE}/daily-notes/weekly-reports/${encodeURIComponent(reportId)}`,
          data,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    create: (data) => post("/daily-notes", data),
    update: (noteId, data) =>
      put(`/daily-notes/${encodeURIComponent(noteId)}`, data),
    delete: (noteId) => remove(`/daily-notes/${encodeURIComponent(noteId)}`),
  },

  residentClockInOut: {
    list: (params = {}) => list("/resident-checkoutin", params),
    page: (params = {}) => list("/resident-checkoutin/page", params),
    listForResident: (residentId, params = {}) =>
      list(
        `/resident-checkoutin/residents/${requiredId(residentId, "Resident id")}`,
        params,
      ),
    currentForResident: (residentId, params = {}) =>
      list(
        `/resident-checkoutin/residents/${requiredId(residentId, "Resident id")}/current`,
        params,
      ),
    clockOutForResident: (residentId, data, params = {}) =>
      apiClient
        .post(
          `${BASE}/resident-checkoutin/residents/${requiredId(residentId, "Resident id")}/check-out`,
          data,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    clockIn: (recordId, data, params = {}) =>
      apiClient
        .put(
          `${BASE}/resident-checkoutin/${requiredId(recordId, "Resident CheckInOut record id")}/check-in`,
          data,
          careRequestConfig({ params }),
        )
        .then(unwrap),
  },

  careStaffAssignments: {
    list: (params) => list("/care-staff-assignments", params),
    create: (data) => post("/care-staff-assignments", data),
    update: (assignmentId, data) =>
      put(`/care-staff-assignments/${encodeURIComponent(assignmentId)}`, data),
    delete: (assignmentId) =>
      remove(`/care-staff-assignments/${encodeURIComponent(assignmentId)}`),
  },

  careProfiles: {
    list: (params) => list("/care-profiles", params),
    search: (params) => list("/care-profiles/search", params),
    quickSearch: (params) => list("/care-profiles/quick-search", params),
    get: (careProfileId) =>
      get(`/care-profiles/${requiredId(careProfileId, "CareProfile id")}`),
    getMine: ({ actorId, ...params } = {}) =>
      apiClient
        .get(`${BASE}/care-profiles/me`, actorConfig(actorId, { params }))
        .then(unwrap),
    create: ({ actorId, ...data } = {}) =>
      apiClient
        .post(`${BASE}/care-profiles`, data, actorConfig(actorId))
        .then(unwrap),
    update: (careProfileId, data) =>
      put(
        `/care-profiles/${requiredId(careProfileId, "CareProfile id")}`,
        data,
      ),
    patchProperties: (careProfileId, updates, { actorId } = {}) =>
      apiClient
        .patch(
          `${BASE}/care-profiles/${requiredId(careProfileId, "CareProfile id")}/properties`,
          updates,
          actorConfig(actorId),
        )
        .then(unwrap),
    getSnapshot,
    saveSnapshot: (careProfileId, type, data, { actorId } = {}) =>
      apiClient
        .put(
          `${BASE}/care-profiles/${requiredId(careProfileId, "CareProfile id")}/snapshots/${requiredId(type, "Snapshot type")}`,
          data,
          actorConfig(actorId),
        )
        .then((response) => response?.data),
    saveStep: (careProfileId, stepKey, { actorId, ...data } = {}) =>
      apiClient
        .put(
          `${BASE}/care-profiles/${requiredId(careProfileId, "CareProfile id")}/onboarding/${requiredId(stepKey, "Onboarding step")}`,
          data,
          actorConfig(actorId),
        )
        .then(unwrap),
  },

  timesheets: {
    listForCareProfile: (careProfileId, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .get(
          `${BASE}/timesheets`,
          actorConfig(actorId, {
            params: {
              ...(queryParams || {}),
              careProfileId: requiredId(careProfileId, "CareProfile id"),
            },
          }),
        )
        .then(unwrap);
    },
    createForCareProfile: (
      careProfileId,
      { actorId, ...data } = {},
      params = {},
    ) =>
      apiClient
        .post(
          `${BASE}/timesheets`,
          {
            ...(data || {}),
            careProfileId: requiredId(careProfileId, "CareProfile id"),
          },
          actorConfig(actorId, { params }),
        )
        .then(unwrap),
    list: (params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .get(
          `${BASE}/timesheets`,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
    create: ({ actorId, ...data } = {}, params = {}) =>
      apiClient
        .post(`${BASE}/timesheets`, data, actorConfig(actorId, { params }))
        .then(unwrap),
    update: (timesheetId, data, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .put(
          `${BASE}/timesheets/${requiredId(timesheetId, "Timesheet id")}`,
          data,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
    delete: (timesheetId, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .delete(
          `${BASE}/timesheets/${requiredId(timesheetId, "Timesheet id")}`,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
  },

  attendance: {
    listForCareProfile: (careProfileId, params) =>
      list("/attendance", {
        ...(params || {}),
        careProfileId: requiredId(careProfileId, "CareProfile id"),
      }),
    currentForCareProfile: (careProfileId, params) =>
      list("/attendance/current", {
        ...(params || {}),
        careProfileId: requiredId(careProfileId, "CareProfile id"),
      }),
    list: (params) => list("/attendance", params),
    get: (attendanceId, params) =>
      apiClient
        .get(
          `${BASE}/attendance/${requiredId(attendanceId, "Attendance id")}`,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    clockIn: (data, params) =>
      apiClient
        .post(
          `${BASE}/attendance/clock-in`,
          data,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    clockOut: (attendanceId, data, params) =>
      apiClient
        .put(
          `${BASE}/attendance/${requiredId(attendanceId, "Attendance id")}/clock-out`,
          data,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    reviewLocation: (attendanceId, { actorId, ...data } = {}, params) =>
      apiClient
        .put(
          `${BASE}/attendance/${requiredId(attendanceId, "Attendance id")}/location-review`,
          data,
          actorConfig(actorId, { params }),
        )
        .then(unwrap),
  },

  schedules: {
    listForCareProfile: (careProfileId, params) =>
      list("/schedules", {
        ...(params || {}),
        careProfileId: requiredId(careProfileId, "CareProfile id"),
      }),
    list: (params) => list("/schedules", params),
    create: ({ actorId, ...data } = {}, params) =>
      apiClient
        .post(`${BASE}/schedules`, data, actorConfig(actorId, { params }))
        .then(unwrap),
    update: (scheduleId, { actorId, ...data } = {}, params) =>
      apiClient
        .put(
          `${BASE}/schedules/${requiredId(scheduleId, "Schedule id")}`,
          data,
          actorConfig(actorId, { params }),
        )
        .then(unwrap),
    delete: (scheduleId, { actorId, ...params } = {}) =>
      apiClient
        .delete(
          `${BASE}/schedules/${requiredId(scheduleId, "Schedule id")}`,
          actorConfig(actorId, { params }),
        )
        .then(unwrap),
  },

  activityPlans: {
    list: (params) => list("/activity-plans", params),
    get: (planId, params = {}) =>
      apiClient
        .get(
          `${BASE}/activity-plans/${requiredId(planId, "Home activity plan id")}`,
          careRequestConfig({ params }),
        )
        .then(unwrap),
    create: ({ actorId, ...data } = {}, params = {}) =>
      apiClient
        .post(`${BASE}/activity-plans`, data, actorConfig(actorId, { params }))
        .then(unwrap),
    update: (planId, { actorId, ...data } = {}, params = {}) =>
      apiClient
        .put(
          `${BASE}/activity-plans/${requiredId(planId, "Home activity plan id")}`,
          data,
          actorConfig(actorId, { params }),
        )
        .then(unwrap),
    delete: (planId, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .delete(
          `${BASE}/activity-plans/${requiredId(planId, "Home activity plan id")}`,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
  },

  documents: {
    list: (params) => list("/documents", params),
    get: (documentId) => get(`/documents/${encodeURIComponent(documentId)}`),
    create: (data) => post("/documents", data),
    delete: (documentId) =>
      remove(`/documents/${encodeURIComponent(documentId)}`),
  },

  compliance: {
    list: (params) => list("/compliance/items", params),
    create: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/compliance/items`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
    update: (itemId, data, params = {}) =>
      apiClient
        .put(
          `${BASE}/compliance/items/${requiredId(itemId, "Compliance item id")}`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
    bundle: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/compliance/bundles`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  compliancePolicies: {
    list: (params) => list("/compliance/policies", params),
    create: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/compliance/policies`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
    bulkCreate: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/compliance/policies/bulk`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  complianceInventoryItems: {
    list: (params) => careService.compliancePolicies.list(params),
    create: (data, params = {}) =>
      careService.compliancePolicies.create(data, params),
    bulkCreate: (data, params = {}) =>
      careService.compliancePolicies.bulkCreate(data, params),
  },

  workspaceCompliancePolicies: {
    list: (workspaceId, params = {}) =>
      list(
        `/compliance/workspaces/${requiredId(workspaceId, "Workspace id")}/compliance-policies`,
        params,
      ),
    create: (workspaceId, data, params = {}) =>
      apiClient
        .post(
          `${BASE}/compliance/workspaces/${requiredId(workspaceId, "Workspace id")}/compliance-policies`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
    update: (workspaceId, policyId, data, params = {}) =>
      apiClient
        .put(
          `${BASE}/compliance/workspaces/${requiredId(workspaceId, "Workspace id")}/compliance-policies/${requiredId(policyId, "Compliance policy id")}`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
    setEnabled: (workspaceId, policyId, enabled, params = {}) =>
      apiClient
        .put(
          `${BASE}/compliance/workspaces/${requiredId(workspaceId, "Workspace id")}/compliance-policies/${requiredId(policyId, "Compliance policy id")}/enabled`,
          { enabled: Boolean(enabled) },
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
    setAllEnabled: (workspaceId, enabled, params = {}) =>
      apiClient
        .put(
          `${BASE}/compliance/workspaces/${requiredId(workspaceId, "Workspace id")}/compliance-policies/enabled`,
          { enabled: Boolean(enabled) },
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  compliancePolicyRuns: {
    list: (workspaceId, params = {}) =>
      list(
        `/compliance/workspaces/${requiredId(workspaceId, "Workspace id")}/compliance-policy-runs`,
        params,
      ),
    create: (workspaceId, policyId, data = {}, params = {}) =>
      apiClient
        .post(
          `${BASE}/compliance/workspaces/${requiredId(workspaceId, "Workspace id")}/compliance-policies/${requiredId(policyId, "Compliance policy id")}/runs`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  complianceInventory: {
    list: (params) => list("/compliance/inventory", params),
    create: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/compliance/inventory`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  complianceAlerts: {
    list: (params) => list("/compliance/alerts", params),
    create: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/compliance/alerts`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  incidents: {
    list: (params) => list("/incidents", params),
    create: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/incidents`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
    update: (incidentId, data, params = {}) =>
      apiClient
        .put(
          `${BASE}/incidents/${requiredId(incidentId, "Incident id")}`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  cbhs: {
    list: (params) => list("/cbhs/supervision-records", params),
    create: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/cbhs/supervision-records`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
    generateMonthlyReport: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/cbhs/monthly-reports`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  rnDelegations: {
    list: (params) => list("/rn-delegations", params),
    create: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/rn-delegations`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
    update: (delegationId, data, params = {}) =>
      apiClient
        .put(
          `${BASE}/rn-delegations/${requiredId(delegationId, "RN delegation id")}`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  familyPortal: {
    list: (params) => list("/family-portal/items", params),
    create: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/family-portal/items`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  secureExchange: {
    list: (params) => list("/secure-exchanges", params),
    create: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/secure-exchanges`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  carePlanning: {
    list: (params) => list("/care-planning/items", params),
    create: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/care-planning/items`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  hoursOperations: {
    list: (params) => list("/hours-operations/items", params),
    create: (data, params = {}) =>
      apiClient
        .post(
          `${BASE}/hours-operations/items`,
          data,
          actorConfig(params?.actorId, { params }),
        )
        .then(unwrap),
  },

  workspaceDownloads: {
    list: (params) => list("/downloads", params),
    create: (data) => post("/downloads", data),
    createDirectory: (data) => post("/downloads/directories", data),
    update: (downloadId, data) =>
      put(`/downloads/${requiredId(downloadId, "Download id")}`, data),
    delete: (downloadId) =>
      remove(`/downloads/${requiredId(downloadId, "Download id")}`),
  },

  assessmentExtractions: {
    extractForProcess: (assessmentProcessId, pages) => {
      const encodedProcessId = requiredId(
        assessmentProcessId,
        "Assessment process id",
      );
      const formData = new FormData();
      pages.forEach((page) => formData.append("pages", page, page.name));
      return apiClient
        .post(
          `${BASE}/assessment-processes/${encodedProcessId}/assessment-extractions`,
          formData,
          multipartCareRequestConfig({
            timeout: 30 * 60 * 1000,
          }),
        )
        .then(unwrap);
    },
    extractForAssessment: (assessmentId, pages) => {
      const encodedAssessmentId = requiredId(assessmentId, "Assessment id");
      const formData = new FormData();
      pages.forEach((page) => formData.append("pages", page, page.name));
      return apiClient
        .post(
          `${BASE}/resident-assessments/${encodedAssessmentId}/extractions`,
          formData,
          multipartCareRequestConfig({
            timeout: 30 * 60 * 1000,
          }),
        )
        .then(unwrap);
    },
    getExtractedDocument: (assessmentId) =>
      get(
        `/resident-assessments/${requiredId(assessmentId, "Assessment id")}/extracted-document`,
      ),
    updateExtractedDocument: (assessmentId, data) =>
      put(
        `/resident-assessments/${requiredId(assessmentId, "Assessment id")}/extracted-document`,
        data,
      ),
  },

  waterTemperatureReadings: {
    list: (params) => list("/water-temperature-readings", params),
    get: (readingId, params) =>
      list(
        `/water-temperature-readings/${requiredId(readingId, "Water temperature reading id")}`,
        params,
      ),
    create: (data, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .post(
          `${BASE}/water-temperature-readings`,
          data,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
    update: (readingId, data, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .put(
          `${BASE}/water-temperature-readings/${requiredId(readingId, "Water temperature reading id")}`,
          data,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
    delete: (readingId, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .delete(
          `${BASE}/water-temperature-readings/${requiredId(readingId, "Water temperature reading id")}`,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
  },

  // annual license records
  workspaceLicenseRecords: {
    list: (params) => list("/workspace-license-records", params),
    get: (recordId, params) =>
      list(
        `/workspace-license-records/${requiredId(recordId, "License record id")}`,
        params,
      ),
    create: (data, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .post(
          `${BASE}/workspace-license-records`,
          data,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
    update: (recordId, data, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .put(
          `${BASE}/workspace-license-records/${requiredId(recordId, "License record id")}`,
          data,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
    delete: (recordId, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .delete(
          `${BASE}/workspace-license-records/${requiredId(recordId, "License record id")}`,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
  },

  complianceQuestionnaireResponses: {
    list: (params) => list("/compliance-questionnaire-responses", params),
    get: (id, params) =>
      list(
        `/compliance-questionnaire-responses/${requiredId(id, "Response id")}`,
        params,
      ),
    create: (data, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .post(
          `${BASE}/compliance-questionnaire-responses`,
          data,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
    update: (id, data, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .put(
          `${BASE}/compliance-questionnaire-responses/${requiredId(id, "Response id")}`,
          data,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
    delete: (id, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .delete(
          `${BASE}/compliance-questionnaire-responses/${requiredId(id, "Response id")}`,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
  },
};

export default careService;
